import { useCallback, useEffect, useRef, useState } from 'react';

import { MytaverseLogger } from '@helpers/logger';
import { WebsocketAction } from '@interfaces/webSocketConnectionInfo';

import { StreamingProviders } from '../interfaces';

export const BILLING_HEARTBEAT_INTERVAL_MS = 10000;

export enum BillingCloseReason {
  LeftEvent = 'LEFT_EVENT',
  Crash = 'CRASH',
  StreamTimeout = 'STREAM_TIMEOUT',
  UnknownFailure = 'UNKNOWN_FAILURE',
}

interface IUseBillingInvoiceProps {
  isEventRun: boolean;
  streamingProvider: StreamingProviders | null;
  streamSessionId: string | null;
  invoiceId: string | null;
  isGamecastCrash: boolean;
  isGamecastTimeout: boolean;
  isWebsocketOpen: boolean;
  sendJSONMessageToWebSocket: (message: unknown) => void;
}

export const useBillingInvoice = ({
  isEventRun,
  streamingProvider,
  streamSessionId,
  invoiceId,
  isGamecastCrash,
  isGamecastTimeout,
  isWebsocketOpen,
  sendJSONMessageToWebSocket,
}: IUseBillingInvoiceProps) => {
  const hasOpenInvoiceRef = useRef(false);
  const prevIsEventRunRef = useRef(false);
  const firstBeatSentIdRef = useRef<string | null>(null);
  const closeReasonRef = useRef<BillingCloseReason | null>(null);
  const [hasOpenInvoice, setHasOpenInvoice] = useState(false);

  const invoiceIdRef = useRef(invoiceId);
  const streamSessionIdRef = useRef(streamSessionId);
  const sendRef = useRef(sendJSONMessageToWebSocket);
  const isSocketOpenRef = useRef(isWebsocketOpen);

  useEffect(() => {
    invoiceIdRef.current = invoiceId;
  }, [invoiceId]);

  useEffect(() => {
    streamSessionIdRef.current = streamSessionId;
    sendRef.current = sendJSONMessageToWebSocket;
    isSocketOpenRef.current = isWebsocketOpen;
  }, [streamSessionId, sendJSONMessageToWebSocket, isWebsocketOpen]);

  useEffect(() => {
    if (isGamecastCrash) {
      closeReasonRef.current = BillingCloseReason.Crash;
    } else if (isGamecastTimeout) {
      closeReasonRef.current = BillingCloseReason.StreamTimeout;
    }
  }, [isGamecastCrash, isGamecastTimeout]);

  const openInvoice = useCallback(() => {
    // A beat still carrying the replaced id would be answered about the dead
    // invoice, and that answer would open a third one.
    invoiceIdRef.current = null;
    firstBeatSentIdRef.current = null;
    // An earlier failure must not become this invoice's end reason.
    closeReasonRef.current = null;

    sendRef.current({
      action: WebsocketAction.BillingOpen,
      streamProvider: streamingProvider,
      streamSessionId: streamSessionIdRef.current ?? undefined,
      heartbeatIntervalMs: BILLING_HEARTBEAT_INTERVAL_MS,
    });

    hasOpenInvoiceRef.current = true;
    setHasOpenInvoice(true);
  }, [streamingProvider]);

  const closeInvoice = useCallback((reason: BillingCloseReason) => {
    if (!hasOpenInvoiceRef.current) {
      return;
    }

    hasOpenInvoiceRef.current = false;
    setHasOpenInvoice(false);

    sendRef.current({
      action: WebsocketAction.BillingClose,
      invoiceId: invoiceIdRef.current ?? undefined,
      reason,
    });

    invoiceIdRef.current = null;
    firstBeatSentIdRef.current = null;
  }, []);

  const handleStale = useCallback(
    (staleInvoiceId?: string) => {
      if (!hasOpenInvoiceRef.current) {
        MytaverseLogger.debug(
          `Billing stale answer ignored, no invoice is open: ${
            staleInvoiceId ?? '(none sent)'
          }`,
        );

        return;
      }

      if (staleInvoiceId && staleInvoiceId !== invoiceIdRef.current) {
        MytaverseLogger.debug(
          `Billing stale answer ignored, it names a previous invoice: ${staleInvoiceId}, metering ${invoiceIdRef.current}`,
        );

        return;
      }

      MytaverseLogger.debug(
        `Billing invoice can no longer accrue, metering the rest of the visit under a new one: ${
          staleInvoiceId ?? '(none sent)'
        }`,
      );

      openInvoice();
    },
    [openInvoice],
  );

  useEffect(() => {
    if (streamingProvider !== StreamingProviders.GameCast) {
      return;
    }

    if (isEventRun && !prevIsEventRunRef.current) {
      prevIsEventRunRef.current = true;
      openInvoice();

      return;
    }

    if (!isEventRun && prevIsEventRunRef.current) {
      prevIsEventRunRef.current = false;
      closeInvoice(closeReasonRef.current ?? BillingCloseReason.UnknownFailure);
    }
  }, [isEventRun, streamingProvider, openInvoice, closeInvoice]);

  // The interval's first tick is a period away, so a shorter visit would take
  // no beat and be valued at nothing. Beaten off the ref, not the prop: the
  // prop is never cleared and still names the previous invoice after a
  // restart. Socket check as in the interval: a queued beat replays at arrival.
  useEffect(() => {
    if (
      !invoiceId ||
      !hasOpenInvoice ||
      !isSocketOpenRef.current ||
      invoiceIdRef.current !== invoiceId ||
      firstBeatSentIdRef.current === invoiceId
    ) {
      return;
    }

    firstBeatSentIdRef.current = invoiceId;

    sendRef.current({
      action: WebsocketAction.BillingBeat,
      invoiceId,
    });
  }, [invoiceId, hasOpenInvoice]);

  useEffect(() => {
    if (!hasOpenInvoice) {
      return;
    }

    const interval = setInterval(() => {
      // Skipped, not queued: the sender replays on reconnect and the server
      // stamps arrival, so a replay claims presence after the visitor left.
      if (!isSocketOpenRef.current) {
        return;
      }

      sendRef.current({
        action: WebsocketAction.BillingBeat,
        invoiceId: invoiceIdRef.current ?? undefined,
      });
    }, BILLING_HEARTBEAT_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [hasOpenInvoice]);

  useEffect(() => {
    return () => {
      closeInvoice(BillingCloseReason.LeftEvent);
    };
  }, [closeInvoice]);

  return { handleStale };
};
